import {
  collection,
  doc,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
} from 'firebase/firestore';

import {
  boardSchema,
  type Board,
  type BoardStatus,
  type TournamentId,
  type Uid,
} from '@/core/schemas';
import { toAppError, type AppError } from '@/infrastructure/errors/AppError';
import { firestore } from '@/infrastructure/firebase/app';
import { createConverter } from '@/infrastructure/firestore/converter';
import { firestorePaths } from '@/infrastructure/firestore/paths';

const converter = createConverter(boardSchema, 'boards');

/**
 * Live subscription to every physical board of the tournament, ordered by
 * number so the list matches the room.
 */
export function subscribeBoards(
  tournamentId: TournamentId,
  onData: (boards: Board[]) => void,
  onError: (error: AppError) => void,
): () => void {
  const boards = query(
    collection(firestore, firestorePaths.boards(tournamentId)).withConverter(converter),
    orderBy('number'),
  );

  return onSnapshot(
    boards,
    (snapshot) => {
      try {
        onData(snapshot.docs.map((document) => document.data()));
      } catch (error) {
        onError(toAppError(error));
      }
    },
    (error) => {
      onError(toAppError(error));
    },
  );
}

/**
 * Sets a board's operational status.
 *
 * `disabled` is the interesting one at the venue: a broken board must be
 * excluded from assignment without renumbering the rest of the room. Kept on
 * the board document rather than derived from the match table so a board can
 * be marked out before any match points at it.
 */
export async function setBoardStatus(input: {
  board: Board;
  status: BoardStatus;
  actorUid: Uid;
}): Promise<void> {
  const { board, status, actorUid } = input;

  try {
    await updateDoc(doc(firestore, firestorePaths.board(board.tournamentId, board.id)), {
      status,
      updatedAt: serverTimestamp(),
      updatedBy: actorUid,
    });
  } catch (error) {
    throw toAppError(error);
  }
}
